import { clamp, HintAnimation, interpolate } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  DIVIDER_V,
  FIELD_SHADOW,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbi3d,
  PRESSED,
  reliefFolder,
  Refs,
  renderCamera,
  renderCursor,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 1 „Erstelle eine Wissensbasis" — Port des Claude-Design-
 * Films `Wissensbasis Loop.dc.html` (Projekt „Portal-Hinweis 1", Datei
 * `wissensbasis-loop.jsx`). Bühne 640×354, ein Durchlauf 8,0 s, Loop.
 *
 * Choreografie und Werte sind 1:1 übernommen; nur das Rendern ist anders:
 * statt React baut `mount` das DOM einmal, `render` setzt je Bild nur die
 * veränderlichen Styles. Zeitachse wie im Entwurf: T ist die *Autorenzeit*
 * (5,0 s, Szenen-Anker `nat`), abgespielt wird jede Szene um 1,6 gestreckt
 * (`dur`) — T = Abspielzeit / 1,6. Neue Fassung aus Claude Design: Werte in
 * `frame()` und die Szenen-Anker (CUES) nachziehen.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Oeffnen: 0.6, Ordner: 1.0, Ordnerzeile: 1.9, Hinzufuegen: 2.3, Fertig: 3.8, Loop: 4.4 };

const FIELD = `background: rgb(255 255 255 / .5); box-shadow: ${FIELD_SHADOW};`;

// Panel-Geometrie (Bühnenkoordinaten)
const PX = 20, PY = 18, PW = 600, PH = 318;
const NAV: [string, string][] = [['agent', 'KI-Agenten'], ['brain', 'Wissensbasis'], ['brush', 'Chat Styling']];
const SEL = 1;
const NAV_Y = (i: number) => 24 + i * 42;
const TABS: [string, number, number][] = [['Dateien', 188, 74], ['Webseiten', 262, 94], ['Kurse', 356, 62]];
const ROWS: [string, string][] = [
  ['file', 'Skript_Kapitel1.pdf'],
  ['globe', 'www.beispiel-hochschule.de'],
  ['courses', 'Moodle · Statistik I'],
];
const ROW_Y = (i: number) => 176 + i * 42;
const DIALOG = { x: 252, y: 102, w: 300, h: 170 };
const TYPED_WORD = 'Prüfungen';

function template(uid: string): string {
  const nav = NAV.map(
    ([ic, label], i) =>
      `<div style="${abs(14, NAV_Y(i), 140, 36, 'display:flex;')}">` +
      (i === SEL ? `<div data-r="navSel" style="position:absolute;inset:0;border-radius:14px;opacity:0;${PRESSED}"></div>` : '') +
      `<div ${i === SEL ? 'data-r="navLabel"' : ''} style="position:relative;flex:1;display:flex;align-items:center;gap:10px;padding:0 12px;font-size:14px;font-weight:500;color:${C.text};white-space:nowrap;">` +
      `${icon(ic)}${label}${i === 0 ? icon('plus', 16, C.icon, 1.8, 'margin-left:auto;') : ''}</div></div>`,
  ).join('');

  const header =
    `<div data-r="header" style="opacity:0;">` +
    `<div style="${abs(180, 22, 216, 34, `border-radius:999px;${FIELD}display:flex;align-items:center;gap:8px;padding:0 14px;box-sizing:border-box;font-size:13px;font-weight:400;color:${C.muted};`)}">${icon('search', 16)}Suchen</div>` +
    `<div data-r="plus" style="${abs(410, 22, 34, 34, `border-radius:999px;background:${C.action};display:flex;align-items:center;justify-content:center;box-shadow:0 6px 12px -6px rgb(37 99 235 / .6);`)}">${icon('plus', 18, '#fff', 2.2)}</div>` +
    `<div style="${abs(180, 68, 404, 2, `background:${DIVIDER_H};`)}"></div></div>`;

  const relief = reliefFolder(
    `<div data-r="folderShut" style="position:absolute;inset:0;">${icon('folder', 20)}</div>`,
    `<div data-r="folderOpen" style="position:absolute;inset:0;opacity:0;">${icon('folderOpen', 20)}</div>`,
  );

  const folderArea =
    `<div data-r="folderRow" style="${abs(180, 80, 404, 44, 'opacity:0;')}">` +
    `<div data-r="folderSel" style="position:absolute;inset:0;border-radius:14px;opacity:0;${PRESSED}"></div>` +
    `<div style="position:relative;height:100%;display:flex;align-items:center;gap:12px;padding:0 8px;">${relief}` +
    `<span style="font-size:15px;font-weight:600;color:${C.text};">Prüfungen</span></div></div>` +
    `<div data-r="tabs" style="opacity:0;">` +
    TABS.map(
      ([label, x, w]) =>
        `<div data-r="tab" style="${abs(x, 134, w, 30, `display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:500;color:${C.muted};`)}">${label}</div>`,
    ).join('') +
    `<div style="${abs(180, 166, 404, 2, `background:${DIVIDER_H};`)}"></div>` +
    `<div data-r="tabUnder" style="${abs(TABS[0][1] + 10, 164, TABS[0][2] - 20, 3, `border-radius:2px;background:${C.action};opacity:0;`)}"></div></div>`;

  const rows =
    `<div data-r="rows">` +
    ROWS.map(
      ([ic, name], i) =>
        `<div data-r="row" style="${abs(180, ROW_Y(i), 404, 42, 'opacity:0;transform-origin:100% 0;')}">` +
        `<div style="height:40px;display:flex;align-items:center;gap:12px;padding:0 12px;">${icon(ic)}` +
        `<span style="flex:1;font-size:14px;font-weight:500;color:${C.text};white-space:nowrap;">${name}</span>` +
        `<div style="position:relative;width:78px;height:24px;">` +
        `<div data-r="track" style="${abs(8, 10, 62, 4, 'border-radius:999px;background:rgb(83 109 145 / .16);')}">` +
        `<div data-r="bar" style="width:0;height:100%;border-radius:999px;background:${C.accent};"></div></div>` +
        `<div data-r="ready" style="${abs(0, 0, 78, 24, `border-radius:999px;background:rgb(0 163 92 / .13);display:flex;align-items:center;justify-content:center;gap:6px;opacity:0;font-size:12px;font-weight:600;color:${C.okInk};`)}">` +
        `<span style="width:8px;height:8px;border-radius:999px;background:${C.ok};"></span>Bereit</div></div></div>` +
        (i < 2 ? `<div style="height:2px;background:${DIVIDER_H};"></div>` : '') +
        `</div>`,
    ).join('') +
    `</div>`;

  const orbi =
    `<div data-r="orbi" style="${abs(548, 22, 34, 34, 'opacity:0;')}">` +
    orbi3d(34, 'eye') +
    `</div>`;

  const panel = glass(
    30,
    0.42,
    `left:${PX}px;top:${PY}px;width:${PW}px;height:${PH}px;`,
    nav + `<div style="${abs(164, 20, 2, PH - 40, `background:${DIVIDER_V};`)}"></div>` + header + folderArea + rows + orbi,
  );

  const dialog =
    `<div data-r="dialog" style="position:absolute;left:${DIALOG.x}px;top:${DIALOG.y}px;width:${DIALOG.w}px;height:${DIALOG.h}px;opacity:0;transform-origin:50% 40%;">` +
    glass(
      20,
      0.78,
      'inset:0;',
      `<div style="${abs(20, 18, 260, 22, `font-size:16px;font-weight:600;color:${C.text};`)}">Ordner erstellen</div>` +
        `<div style="${abs(20, 52, 260, 16, `font-size:12px;font-weight:500;color:${C.muted};`)}">Name</div>` +
        `<div data-r="field" style="${abs(20, 72, 260, 36, `border-radius:999px;background:rgb(255 255 255 / .5);box-shadow:${FIELD_SHADOW}, 0 0 0 2px rgb(59 130 246 / .30);display:flex;align-items:center;padding:0 14px;box-sizing:border-box;font-size:14px;font-weight:400;color:${C.muted};`)}">` +
        `<span data-r="fieldText">z.B. Prüfungen</span><span data-r="caret" style="width:1.5px;height:17px;margin-left:-88px;background:${C.text};"></span></div>` +
        `<div style="${abs(20, 122, 260, 32, 'display:flex;justify-content:flex-end;gap:8px;')}">` +
        `<div style="height:32px;padding:0 16px;border-radius:999px;display:flex;align-items:center;font-size:13px;font-weight:500;color:${C.text};background:rgb(255 255 255 / .55);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 2px 5px -2px rgb(38 65 112 / .25);">Abbrechen</div>` +
        `<div data-r="create" style="height:32px;padding:0 18px;border-radius:999px;display:flex;align-items:center;font-size:13px;font-weight:600;color:#fff;background:${C.action};box-shadow:0 6px 12px -6px rgb(37 99 235 / .6);">Erstellen</div></div>`,
    ) +
    `</div>`;

  return stage(panel + dialog + cursorTemplate(uid));
}

export class CreateKnowledgeBaseAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: Ordner mit drei Inhalten, Orbi hat genickt (T = 4,3). */
  readonly stillAt = 4.3 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-kb')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in wissensbasis-loop.jsx. */
  private frame(T: number): void {
    const { Oeffnen: oe, Ordner: od, Ordnerzeile: oz, Hinzufuegen: hz, Fertig: fe, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.5, T);

    const k1 = click(oe - 0.02, T), k2 = click(od, T), k3 = click(od + 0.7, T);
    const press = Math.max(k1.press, k2.press, k3.press);
    const ring = [k1, k2, k3].map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;

    const ct = [0, oe - 0.05, oe + 0.02, od - 0.05, od + 0.05, od + 0.3, od + 0.4, od + 0.66, od + 0.76, hz - 0.05, lp, lp + 0.55];
    const cx = MOTION.path(ct, [380, 102, 102, 447, 447, 486, 486, 489, 489, 96, 96, 380], T);
    const cy = MOTION.path(ct, [250, 102, 102, 57, 57, 192, 192, 240, 240, 290, 290, 250], T);

    const sel = MOTION.enter(oe, oe + 0.12, T) * live;
    const header = MOTION.enter(oe + 0.05, oe + 0.35, T) * live;
    const plusHover = T > od - 0.08 && T < od + 0.2;

    const dlgIn = MOTION.enter(od + 0.04, od + 0.24, T);
    const dlgOut = MOTION.glide(od + 0.74, od + 0.9, T);
    const typed = Math.floor(clamp((T - (od + 0.22)) / 0.36, 0, 1) * TYPED_WORD.length);
    const caret = Math.floor(T * 4) % 2 === 0 ? 1 : 0.15;
    const btnHover = T > od + 0.62 && T < od + 0.9;

    const rowIn = MOTION.enter(oz - 0.02, oz + 0.2, T) * live;
    const rowSel = MOTION.enter(oz + 0.1, oz + 0.25, T) * live;
    const open = MOTION.glide(oz + 0.12, oz + 0.34, T) * live;
    const tabsIn = MOTION.enter(oz + 0.1, oz + 0.38, T) * live;
    const tabPos = MOTION.glide(hz + 0.48, hz + 0.66, T) + MOTION.glide(hz + 0.98, hz + 1.16, T);
    const tabUnder = MOTION.enter(oz + 0.2, oz + 0.4, T) * live;

    const orbiIn = MOTION.enter(fe - 0.2, fe, T) * live;
    const nod = clamp((T - (fe + 0.05)) / 0.45, 0, 1);

    const ck = [0, oe, od, od + 0.25, od + 0.85, oz + 0.35, 99];
    const s = MOTION.path(ck, [1, 1.05, 1.04, 1.08, 1.08, 1, 1], T);
    const fx = MOTION.path(ck, [320, 230, 360, 392, 392, 320, 320], T);
    const fy = MOTION.path(ck, [176, 120, 120, 182, 182, 176, 176], T);

    // Kamera
    renderCamera(this.one('camera'), fx, fy, s);

    // Navigation
    this.one('navSel').style.opacity = String(sel);
    this.one('navLabel').style.fontWeight = sel > 0.5 ? '600' : '500';

    // Kopf: Suche + Plus
    const head = this.one('header');
    head.style.opacity = String(header);
    head.style.transform = `translateY(${(1 - header) * 6}px)`;
    const plus = this.one('plus');
    plus.style.background = plusHover ? C.hover : C.action;
    plus.style.transform = `scale(${1 - 0.06 * k2.press})`;

    // Ordnerzeile + Reiter
    const folderRow = this.one('folderRow');
    folderRow.style.opacity = String(rowIn);
    folderRow.style.transform = `translateY(${(1 - rowIn) * 8}px)`;
    this.one('folderSel').style.opacity = String(rowSel);
    this.one('folderShut').style.opacity = String(1 - open);
    this.one('folderOpen').style.opacity = String(open);
    this.one('tabs').style.opacity = String(tabsIn);
    const activeIdx = Math.round(tabPos);
    this.refs['tab'].forEach((el, i) => {
      el.style.fontWeight = i === activeIdx ? '600' : '500';
      el.style.color = i === activeIdx ? C.text : C.muted;
    });
    const under = this.one('tabUnder');
    under.style.left = `${interpolate([0, 1, 2], TABS.map((tb) => tb[1] + 10))(tabPos)}px`;
    under.style.width = `${interpolate([0, 1, 2], TABS.map((tb) => tb[2] - 20))(tabPos)}px`;
    under.style.opacity = String(tabUnder);

    // Tabellenzeilen
    this.one('rows').style.opacity = String(live);
    this.refs['row'].forEach((el, i) => {
      const st = hz + 0.5 * i;
      const fly = MOTION.enter(st, st + 0.3, T);
      const prog = MOTION.glide(st + 0.2, st + 0.4, T);
      const ready = MOTION.enter(st + 0.38, st + 0.46, T);
      el.style.opacity = String(Math.min(1, fly * 1.6));
      el.style.transform = `translate(${(1 - fly) * 150}px, ${(1 - fly) * -70}px) scale(${0.92 + 0.08 * fly})`;
      this.refs['track'][i].style.opacity = String(1 - ready);
      this.refs['bar'][i].style.width = `${prog * 100}%`;
      this.refs['ready'][i].style.opacity = String(ready);
    });

    // Orbi nickt
    const dip = (3 * (1 - Math.cos(2 * Math.PI * 2 * nod))) / 2;
    const orbi = this.one('orbi');
    orbi.style.opacity = String(orbiIn);
    orbi.style.transform = `translateY(${dip + (1 - orbiIn) * 4}px) rotate(${dip * 2}deg)`;
    this.refs['eye'].forEach((el) => el.setAttribute('transform', `translate(0 ${dip * 0.6})`));

    // Dialog „Ordner erstellen"
    const dialog = this.one('dialog');
    dialog.style.opacity = String(dlgIn * (1 - dlgOut));
    dialog.style.transform = `scale(${0.92 + 0.08 * dlgIn - 0.04 * dlgOut})`;
    const word = TYPED_WORD.slice(0, typed);
    const fieldText = this.one('fieldText');
    if (fieldText.textContent !== (word || 'z.B. Prüfungen')) fieldText.textContent = word || 'z.B. Prüfungen';
    this.one('field').style.color = word ? C.text : C.muted;
    const caretEl = this.one('caret');
    caretEl.style.marginLeft = `${word ? 1 : -88}px`;
    caretEl.style.opacity = String(caret);
    const create = this.one('create');
    create.style.background = btnHover ? C.hover : C.action;
    create.style.transform = `scale(${1 - 0.05 * k3.press})`;

    // Zeiger
    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, press, ring);
  }
}
